import { api } from '@/lib/axios';
import type { ModuleKey, ModuleStatus } from '@symphenia/shared-types';

/**
 * Was der Mandant an Modulen hat — Buchung, Laufzeit und Platzstand.
 *
 * Liegt in `lib/`, weil **zwei** Features dieselbe Auskunft brauchen: die
 * Modulseite unter `tenants` und seit AP-29-N4 das Einladungsformular unter
 * `users`, das die Plätze der gebuchten Module zum Ankreuzen anbietet. Ein
 * Querimport zwischen den Features wäre die zweite Wahrheit über dieselbe
 * Antwort — und genau das verbietet die Modulgrenze (AP-21-B).
 */

/** Status einer Buchung, wie ihn der Betreiber setzt (AP-29-B). */
export type ModuleBookingStatus =
  | 'TRIAL'
  | 'ACTIVE'
  | 'SUSPENDED'
  | 'CANCELLED'
  | 'EXPIRED';

/** AP-29-F: Der Stand einer Buchung — was die Modulseite über die Laufzeit sagt. */
export interface ModuleBookingInfo {
  status: ModuleBookingStatus;
  /** Frühestes wirksames Ende als ISO-Datum; null = unbefristet. */
  endsAt: string | null;
  /** Woran das Ende hängt — Testphase, Laufzeit oder Kündigung. */
  endKind: 'TRIAL' | 'VALID_UNTIL' | 'CANCELLED' | null;
  seatMode: 'ALL_USERS' | 'ASSIGNED';
  /** Gekaufte Plätze; null bei „alle Benutzer". */
  seats: number | null;
  seatsUsed: number;
  /** Zuteilungen deaktivierter Benutzer — sie zählen nicht mit. */
  seatsDormant: number;
}

/** AP-29-F: Eine offene Anfrage beim Betreiber (jünger als 24 Stunden). */
export interface ModuleRequestInfo {
  kind: 'MODULE' | 'SEATS' | 'INTEREST';
  requestedAt: string;
  additionalSeats: number | null;
}

export interface ModuleCatalogEntry {
  key: ModuleKey;
  status: ModuleStatus;
  bookable: boolean;
  /** Darf **dieser Benutzer** das Modul benutzen (Buchung ∧ Platz)? */
  active: boolean;
  /**
   * AP-29-D: Hat der **Mandant** es gebucht — unabhängig von Plätzen?
   * Der Unterschied zu `active` ist der zwischen „nicht gebucht"
   * (→ Vertrieb) und „kein Platz" (→ eigener Administrator).
   */
  bookedForTenant: boolean;
  /** Null, wenn es zu diesem Modul nie eine Buchung gab. */
  booking: ModuleBookingInfo | null;
  lastRequest: ModuleRequestInfo | null;
  /**
   * AP-29-N-H: Zu welcher Gruppe dieser Eintrag gehört — `null` bei einem
   * eigenständigen Modul. Die Seite staffelt danach; sie zerlegt **nicht** den
   * Schlüssel am Punkt, der ist nur für Menschen da.
   */
  parent: ModuleKey | null;
  /**
   * Bei einer **Gruppe**: wie viele ihrer Teile der Betrieb hat. Das beantwortet
   * die vierte Frage, die es vor der Zerlegung nicht geben konnte — *was fehlt
   * mir noch*.
   */
  parts: { total: number; booked: number } | null;
  /**
   * AP-29-N: Wie der Posten **nach außen heißt** — was der Betreiber im Katalog
   * eingetragen hat. `null` = es gilt die i18n-Vorgabe. Entschieden wird das
   * in `lib/module-label.ts`, nicht hier.
   */
  salesName: string | null;
}

/** AP-11-C/AP-29: Die Antwort von `GET /tenants/me/modules`. */
export interface TenantModulesResponse {
  active: string[];
  available: string[];
  /** AP-29-F: Aktive Benutzer des Mandanten — macht aus „alle Benutzer" eine Zahl. */
  activeUsers?: number;
  catalog: ModuleCatalogEntry[];
}

export const tenantModulesApi = {
  /**
   * AP-11-C: Liste aller Module mit Aktiv-/Verfügbar-Status.
   * AP-29-A: zusätzlich `catalog` — Reifegrad je Modul aus der Registry.
   * AP-29-F: dazu `booking` (Laufzeit, Platzstand) und `lastRequest`.
   *
   * **ADMIN-only.** Ein Aufrufer unter ADMIN bekommt 403 und muss das aushalten,
   * statt die Seite daran scheitern zu lassen.
   */
  getModules: () => api.get<TenantModulesResponse>('/tenants/me/modules'),
};
